<template>
  <section class="company-logo" :aria-label="$t('lang_ACCOUNT_COMPANIES_LOGO_CAPTION_TITLE')" :aria-busy="busy || loading">
    <div class="company-logo__heading">{{ $t('lang_ACCOUNT_COMPANIES_LOGO_CAPTION_TITLE') }}</div>
    <button type="button" class="company-logo__drop" :class="{ 'company-logo__drop--active': dragging }"
      :disabled="busy || loading || forbidden" :aria-label="$t(preview ? 'lang_ACCOUNT_COMPANIES_LOGO_BUTTON_REPLACE' : 'lang_ACCOUNT_COMPANIES_LOGO_BUTTON_UPLOAD')"
      @click="$refs.file.click()" @dragover.prevent="dragging = !busy && !loading && !forbidden"
      @dragleave.prevent="dragging = false" @drop.prevent="drop">
      <v-progress-circular v-if="loading" indeterminate size="26" width="2" color="primary" />
      <template v-else-if="preview">
        <img :src="preview" :alt="$t('lang_ACCOUNT_COMPANIES_LOGO_CAPTION_TITLE')" class="company-logo__preview" />
        <span class="company-logo__replace"><v-icon small>edit</v-icon> {{ $t('lang_ACCOUNT_COMPANIES_LOGO_BUTTON_REPLACE') }}</span>
      </template>
      <template v-else>
        <span class="company-logo__icon"><v-icon color="primary" size="24">add_photo_alternate</v-icon></span>
        <strong>{{ $t('lang_ACCOUNT_COMPANIES_LOGO_BUTTON_UPLOAD') }}</strong>
        <span class="company-logo__hint">{{ $t('lang_ACCOUNT_COMPANIES_LOGO_INFO_DROP') }}</span>
      </template>
      <div v-if="busy" class="company-logo__overlay"><v-progress-circular indeterminate size="28" width="2" color="primary" /><span>{{ $t('lang_ACCOUNT_COMPANIES_LOGO_STATUS_SAVING') }}</span></div>
    </button>
    <input ref="file" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg" class="company-logo__input" tabindex="-1" @change="choose" />
    <p class="company-logo__help">{{ $t('lang_ACCOUNT_COMPANIES_LOGO_INFO_FORMATS') }}</p>
    <p class="company-logo__help">{{ $t('lang_ACCOUNT_COMPANIES_LOGO_INFO_DESCRIPTION') }}</p>
    <div class="company-logo__actions">
      <span role="status" class="company-logo__saved"><template v-if="saved"><v-icon small color="success">check_circle_outline</v-icon> {{ $t('lang_ACCOUNT_COMPANIES_LOGO_STATUS_SAVED') }}</template></span>
      <v-btn v-if="preview && !confirmRemove" text small color="error" :disabled="busy || loading" @click="confirmRemove = true">{{ $t('lang_ACCOUNT_COMPANIES_LOGO_BUTTON_REMOVE') }}</v-btn>
    </div>
    <div v-if="confirmRemove" class="company-logo__confirmation">
      <span>{{ $t('lang_ACCOUNT_COMPANIES_LOGO_CONFIRM_REMOVE') }}</span>
      <div><v-btn text small :disabled="busy" @click="confirmRemove = false">{{ $t('lang_ACCOUNT_COMPANIES_LOGO_BUTTON_CANCEL') }}</v-btn><v-btn text small color="error" :loading="busy" @click="remove">{{ $t('lang_ACCOUNT_COMPANIES_LOGO_BUTTON_REMOVE') }}</v-btn></div>
    </div>
    <p v-if="error" role="alert" class="company-logo__error">{{ $t(errorKey) }} <v-btn v-if="loadFailed" text small @click="load">{{ $t('lang_ACCOUNT_COMPANIES_LOGO_BUTTON_RETRY') }}</v-btn></p>
  </section>
</template>
<script>
import axios from 'axios'
import api from '@/api_alv'

export default {
  name: 'CompanyLogoUpload',
  computed: {
    endpoint() { return api.rootUrl + 'account/company-logo' },
    errorKey() { return {"forbidden": "lang_ACCOUNT_COMPANIES_LOGO_ERROR_FORBIDDEN", "size": "lang_ACCOUNT_COMPANIES_LOGO_ERROR_SIZE", "type": "lang_ACCOUNT_COMPANIES_LOGO_ERROR_TYPE", "dimensions": "lang_ACCOUNT_COMPANIES_LOGO_ERROR_DIMENSIONS", "failed": "lang_ACCOUNT_COMPANIES_LOGO_ERROR_FAILED", "oneFile": "lang_ACCOUNT_COMPANIES_LOGO_ERROR_ONE_FILE"}[this.error] || 'lang_ACCOUNT_COMPANIES_LOGO_ERROR_FAILED' }
  },
  data() { return { preview: '', loading: true, busy: false, dragging: false, error: '', saved: false, confirmRemove: false, forbidden: false, loadFailed: false } },
  created() { this.cancel = axios.CancelToken.source(); this.disposed = false; this.load() },
  beforeDestroy() { this.disposed = true; this.cancel.cancel(); this.setPreview('') },
  methods: {
    config() { return { ...api.getConfig(), responseType: 'blob', cancelToken: this.cancel.token } },
    setPreview(url) { if (this.preview) URL.revokeObjectURL(this.preview); this.preview = url },
    async report(error) {
      if (axios.isCancel(error) || this.disposed) return;
      if (error.response && error.response.status === 403) { this.forbidden = true; this.error = 'forbidden'; return }
      let code = '';
      try { code = JSON.parse(await error.response.data.text()).code } catch (_) { /* Network or non-JSON response. */ }
      if (this.disposed) return;
      this.error = ({ logo_size: 'size', logo_type: 'type', logo_dimensions: 'dimensions', logo_upload: 'failed' })[code] || 'failed';
    },
    async load() {
      if (this.busy) return;
      this.loading = true; this.error = ''; this.loadFailed = false;
      try {
        const { data, status } = await axios.get(this.endpoint, this.config());
        if (!this.disposed) this.setPreview(status === 204 ? '' : URL.createObjectURL(data));
      } catch (error) { this.loadFailed = true; await this.report(error) }
      finally { this.loading = false }
    },
    choose(event) { const files = event.target.files; if (files && files.length) this.upload(files[0]); event.target.value = '' },
    drop(event) {
      this.dragging = false;
      if (this.busy || this.loading || this.forbidden) return;
      const files = event.dataTransfer.files;
      if (files.length !== 1) { this.error = 'oneFile'; return }
      this.upload(files[0]);
    },
    async upload(file) {
      if (this.busy || this.loading || this.forbidden) return;
      this.error = ''; this.saved = false; this.confirmRemove = false;
      if (!['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(file.type) && !(file.type === '' && /\.svg$/i.test(file.name))) { this.error = 'type'; return }
      if (!file.size || file.size > 5 * 1024 * 1024) { this.error = 'size'; return }
      this.busy = true;
      const form = new FormData(); form.append('logo', file);
      try {
        const { data } = await axios.post(this.endpoint, form, this.config());
        if (!this.disposed) { this.setPreview(URL.createObjectURL(data)); this.saved = true; this.loadFailed = false }
      } catch (error) { await this.report(error) }
      finally { this.busy = false }
    },
    async remove() {
      if (this.busy) return;
      this.busy = true; this.error = ''; this.saved = false;
      try { await axios.delete(this.endpoint, this.config()); if (!this.disposed) { this.setPreview(''); this.saved = true; this.confirmRemove = false } }
      catch (error) { await this.report(error) }
      finally { this.busy = false }
    }
  }
}
</script>
<style scoped>
.company-logo { margin: 4px 4px 0; min-width: 0; }
.company-logo__heading { color: #6f6b76; font-size: 12px; margin-bottom: 10px; }
.company-logo__drop { position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; width: 100%; min-height: 128px; padding: 16px; border: 1.5px dashed #d4c8d9; border-radius: 12px; background: #fcfafc; color: #4c3656; transition: border-color .16s, background .16s; cursor: pointer; }
.company-logo__drop:hover, .company-logo__drop--active { border-color: #9865a1; background: #f6eff8; }
.company-logo__drop:focus-visible { outline: 3px solid #b180b9; outline-offset: 3px; }
.company-logo__drop:disabled { cursor: default; }
.company-logo__drop strong { font-size: 13px; font-weight: 500; }
.company-logo__icon { display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 10px; background: #f0e7f3; }
.company-logo__hint, .company-logo__replace { font-size: 11px; color: #88798e; }
.company-logo__preview { display: block; max-width: 100%; height: 76px; object-fit: contain; }
.company-logo__input { display: none; }
.company-logo__help { font-size: 11px; color: #87828b; line-height: 1.5; margin: 7px 0 0; }
.company-logo__actions { min-height: 32px; display: flex; align-items: center; justify-content: space-between; }
.company-logo__saved { font-size: 11px; color: #3f7854; }
.company-logo__overlay { position: absolute; inset: 0; background: rgba(252,250,252,.94); border-radius: 12px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; font-size: 12px; }
.company-logo__confirmation { font-size: 12px; padding: 10px; background: #fcf5f5; border-radius: 8px; }
.company-logo__confirmation > div { display: flex; justify-content: flex-end; }
.company-logo__error { color: #b33040; font-size: 12px; margin: 6px 0 0; }
</style>
